import React, { useContext, useMemo, useState } from "react";
import { Trash2, Zap } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import EntityFormModal from "modules/swiftboard/components/EntityFormModal";
import { GlobalAdvanceFilter } from "modules/swiftboard/components/advance-filter";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import { AuthContext } from "context/AuthContext";
import useConfirm from "../../components/useConfirm";
import useServerList from "../../components/useServerList";
import { MANAGE_ROLE } from "../../features/builder.constants";
import { errorMessage } from "../../features/builder.utils";
import apiReminderDomainEvent from "./api";
import { domainEventFields } from "./features/domain-event.fields";
import { domainEventValidation } from "./features/domain-event.validation";
import { loadDomainEventDependencies } from "./features/domain-event.dependencies";
import { domainEventFilterFields, loadDomainEventFilterDependencies } from "./features/domain-event.filter";
import { toDomainEventPayload } from "./features/domain-event.utils";
import { getDomainEventColumns } from "./domain-event-list/domainEventColumns";
import DomainEventRowActions from "./domain-event-list/DomainEventRowActions";
import DomainEventViewDrawer from "./domain-event-dialogs/DomainEventViewDrawer";

const createDomainEvent = (values) => apiReminderDomainEvent.create(toDomainEventPayload(values));
const updateDomainEvent = (id, values) => apiReminderDomainEvent.update(id, toDomainEventPayload(values));

// Reminder Setup -> Domain Event (tbl_rem_domain_event): events application code emits that can start a reminder,
// e.g. a project status change. Each points at the source whose record it is about.
const DomainEvent = () => {
  const { showAlert } = useAlert();
  const { user } = useContext(AuthContext);
  const canManage = user?.user_role?.role === MANAGE_ROLE;
  const list = useServerList(apiReminderDomainEvent.getAll, { loadError: "Failed to load domain events." });
  const { confirmData, openConfirm, handleConfirmClose } = useConfirm();

  const [form, setForm] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);

  const deleteDomainEvents = async (ids) => {
    const confirmed = await openConfirm({
      title: ids.length > 1 ? "Delete Selected" : "Delete Domain Event",
      message: `Delete ${ids.length > 1 ? `${ids.length} domain events` : "this domain event"}? Events used by a reminder trigger can't be deleted.`,
      confirmText: "Delete",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      const res = await apiReminderDomainEvent.delete(ids);
      showAlert({ type: "success", message: res.data?.message || "Deleted successfully." });
      list.resetSelection();
      list.reload();
    } catch (err) {
      showAlert({ type: "error", message: errorMessage(err, "Delete failed.") });
    }
  };

  const columns = useMemo(() => getDomainEventColumns(list.start), [list.start]);

  const renderRowActions = (row) => (
    <DomainEventRowActions
      row={row}
      canManage={canManage}
      onView={setViewRecord}
      onEdit={(target) => setForm({ id: target.id })}
      onDelete={(target) => deleteDomainEvents([target.id])}
    />
  );

  return (
    <div className="h-full overflow-y-auto">
      <div className="space-y-4 pb-4">
        <TableToolBar
          onSearch={list.handleSearch}
          onRefresh={list.handleRefresh}
          onCreate={() => setForm({ id: null })}
          onDeleteSelected={canManage ? () => deleteDomainEvents(list.selectedRows) : undefined}
          selectedCount={list.selectedRows.length}
          resetSearchTrigger={list.resetTrigger}
          canCreate={canManage}
          resultCount={list.totalCount}
          advanceFilter={<GlobalAdvanceFilter fields={domainEventFilterFields} dependenciesLoader={loadDomainEventFilterDependencies} onApply={list.handleApplyFilters} resetKey={list.resetTrigger} />}
        />

        {!list.loading && list.records.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <NoDataState
              icon={Zap}
              title={list.hasQuery ? "No domain events match" : "No domain events yet"}
              message={
                list.hasQuery
                  ? "No domain events match your search or filters. Try adjusting them."
                  : "Register the events code announces, such as project.status_changed, so a trigger can start a reminder when they happen."
              }
              showBack={false}
              showRefresh={list.hasQuery}
              onRefresh={list.handleRefresh}
              showCreate={canManage && !list.hasQuery}
              createText="Add Domain Event"
              onCreate={() => setForm({ id: null })}
            />
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-border">
              <TableGrid
                data={list.records}
                loading={list.loading}
                columns={columns}
                pageSize={list.query.size}
                sortConfig={list.query.sort}
                onSort={list.handleSort}
                onSelectionChange={list.setSelectedRows}
                resetSelectionTrigger={list.selectionReset}
                selectable={canManage}
                showActions
                renderActions={renderRowActions}
              />
            </div>
            <Pagination totalCount={list.totalCount} pageSize={list.query.size} currentPage={list.query.page} onPageChange={list.handlePageChange} selectedCount={list.selectedRows.length} />
          </>
        )}
      </div>

      <EntityFormModal
        isOpen={Boolean(form)}
        onClose={() => setForm(null)}
        id={form?.id}
        title="Domain Event"
        fetchById={apiReminderDomainEvent.getById}
        createFn={createDomainEvent}
        updateFn={updateDomainEvent}
        dependenciesLoader={loadDomainEventDependencies}
        fields={domainEventFields}
        validationSchema={domainEventValidation}
        onSuccess={list.reload}
      />

      {viewRecord && <DomainEventViewDrawer record={viewRecord} onClose={() => setViewRecord(null)} />}
      {confirmData.isOpen && <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />}
    </div>
  );
};

export default DomainEvent;
